import { useCallback } from 'react';

import { useSession } from '@eneco/auth';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import {
  ActiveProductsCardResponse,
  AdvanceAmountChangedCardResponse,
  ComfortBonusCardResponse,
  CurrentAdvanceAmountVsRecommendedAdvanceAmountCardResponse,
  DeliveryCountdownCardResponse,
  IntermediateSettlementBillInclReasonCardResponse,
  NextBestActionCardResponse,
  NoOpenAmountCardResponse,
  OrderStatusCardResponse,
  PayInvoiceCardResponse,
  PaymentPlanInstallmentCardResponse,
  ProductConfirmationCardResponse,
  PushDirectDebitCardResponse,
  ReferToAccountNumberIfRefundCardResponse,
  ShowPersonalInfoCardResponse,
  ShowUsageCardResponse,
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberContactDetails,
} from '@eneco/dc-be/client';
import useAuthenticatedDCBE from '@eneco/dc-be/hooks/useAuthenticatedDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { useFormatter } from '@eneco/i18n';
import { Bucket } from '@eneco/sparky';
import { styled } from '@eneco/sparky/stitches';
import { useContent } from '@sitecore/common';
import { DashboardOverviewRendering } from '@sitecore/types/DashboardOverview';
import { differenceInCalendarDays, startOfDay } from 'date-fns';

import { ActiveProductsCard } from './ActiveProductsCard';
import { AdvanceAmountChangedCard } from './AdvanceAmountChangedCard';
import { AnticipationAnnouncementCard } from './AnticipationAnnouncementCard';
import { BrandBenefitsCard } from './BrandBenefitsCard';
import { BudgetMeterNotificationCard } from './BudgetMeterNotificationCard';
import { ComfortBonusCard, getComfortBonusLevelMapB2C } from './ComfortBonusCard';
import { CompletedProgressTimelineCard } from './CompletedProgressTimelineCard';
import { CompleteMoveFileCard } from './CompleteMoveFileCard';
import { CurrentAdvanceAmountVsRecommendedAdvanceAmountCard } from './CurrentAdvanceAmountVsRecommendedAdvanceAmountCard';
import { DeliveryCountDownCard } from './DeliveryCountDownCard';
import { FaqCard } from './FaqCard';
import { IntermediateSettlementBillInclReasonCard } from './IntermediateSettlementBillInclReasonCard';
import { ItsmeCard } from './ItsmeCard';
import { MarketingPropositionCard } from './MarketingPropositionCard';
import { MonthlyBillingCard } from './MonthlyBillingCard';
import { MoveFileCreatedCard } from './MoveFileCreatedCard';
import { NoOpenAmountCard } from './NoOpenAmountCard';
import { OrderStatusCard } from './OrderStatusCard';
import { PayInvoiceCard } from './PayInvoiceCard';
import { PaymentMethodChangeFailedCard } from './PaymentMethodChangeFailedCard';
import { PaymentPlanInstallmentCard } from './PaymentPlanInstallmentCard';
import { ProductConfirmationCard } from './ProductConfirmationCard';
import { ProgressTimelineCard } from './ProgressTimelineCard';
import { PushDirectDebitCard } from './PushDirectDebitCard';
import { ReferToAccountNumberIfRefundCard } from './ReferToAccountNumberIfRefundCard';
import { ShowPersonalInfoCard } from './ShowPersonalInfoCard';
import { ShowUsageCard } from './ShowUsageCard';
import { ThankYouCard } from './ThankYouCard';

// This is a workaround because the code generator generates the details as non-nullable, while it can be ommited if there is an error.
const NBA_CARD_KEYS_WITH_DETAILS = new Set<NextBestActionCardResponse['key']>([
  'IntermediateSettlementBillInclReason',
  'PayInvoice',
  'NoOpenAmount',
  'CurrentAdvanceAmountVsRecommendedAdvanceAmount',
  'ComfortBonus',
  'ShowPersonalInfo',
  'PaymentPlanInstallment',
  'ProductConfirmation',
  'ReferToAccountNumberIfRefund',
  'AdvanceAmountChanged',
  'OrderStatus',
  'DeliveryCountdown',
]);

const isMissingRequiredCardDetails = (card: NextBestActionCardResponse) => {
  if (card.key === 'ShowUsage') {
    const showUsageCard = card as ShowUsageCardResponse;
    return !showUsageCard.hasAnalogMeter && showUsageCard.details == null;
  }

  if (!NBA_CARD_KEYS_WITH_DETAILS.has(card.key)) {
    return false;
  }

  return (card as { details?: unknown }).details == null;
};

export const DashboardBlock = ({
  card,
  addressIdentifier,
}: {
  card: NextBestActionCardResponse;
  addressIdentifier: string;
}) => {
  const { format } = useFormatter();
  const { fields } = useContent<DashboardOverviewRendering>();
  const { data: session } = useSession();
  const {
    selectedAccount: { crmAccountNumber: accountNumber },
  } = useSelfServiceAccount();
  const { data: contactDetailsData } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberContactDetails,
    {
      path: {
        accountNumber,
      },
    },
    [`/accounts/${accountNumber}/contact/details`],
    session,
  );
  const { customerType } = unwrapData(contactDetailsData) || {};
  const isB2C = customerType === 'Residential';
  const getCardContent = useCallback(() => {
    switch (card.key) {
      case 'IntermediateSettlementBillInclReason':
        return {
          title: fields.intermediateSettlementBillInclReasonCard?.title.value,
          content: (
            <IntermediateSettlementBillInclReasonCard card={card as IntermediateSettlementBillInclReasonCardResponse} />
          ),
        };
      case 'PayInvoice':
        return {
          title: fields.payInvoiceCard.title.value,
          content: <PayInvoiceCard card={card as PayInvoiceCardResponse} />,
        };
      case 'NoOpenAmount':
        return {
          title: fields.noOpenAmountCard?.title.value,
          content: <NoOpenAmountCard card={card as NoOpenAmountCardResponse} />,
        };
      case 'BudgetMeterNotification':
        return {
          title: fields.currentAdvanceAmountVsRecommended?.prepaymentMeterNotification?.value?.title,
          content: <BudgetMeterNotificationCard card={card} />,
        };
      case 'CurrentAdvanceAmountVsRecommendedAdvanceAmount':
        return {
          title: fields.currentAdvanceAmountVsRecommended?.title.value,
          content: (
            <CurrentAdvanceAmountVsRecommendedAdvanceAmountCard
              card={card as CurrentAdvanceAmountVsRecommendedAdvanceAmountCardResponse}
            />
          ),
        };
      case 'ConfigureItsMe':
        return {
          title: fields.configureItsmeCard?.title.value,
          content: <ItsmeCard />,
        };
      case 'ActiveProducts':
        return {
          title: fields.activeProductsCard?.title.value ?? 'Active Products [not in Sitecore]',
          content: <ActiveProductsCard card={card as ActiveProductsCardResponse} />,
        };
      case 'ComfortBonus': {
        const title = isB2C
          ? getComfortBonusLevelMapB2C(fields).get((card as ComfortBonusCardResponse)?.details?.comfortBonusLevel)
              ?.title
          : fields.comfortBonusCardB2b?.title?.value;
        return {
          title: title || '',
          content: <ComfortBonusCard card={card as ComfortBonusCardResponse} />,
        };
      }
      case 'ShowPersonalInfo':
        return {
          title: fields.showPersonalInfoCard?.title.value ?? 'Show Personal Info [not in Sitecore]',
          content: <ShowPersonalInfoCard card={card as ShowPersonalInfoCardResponse} />,
        };
      case 'PaymentPlanInstallment':
        return {
          title: fields.paymentPlanInstallmentCard?.title.value ?? 'Payment Plan Installment [not in Sitecore]',
          content: <PaymentPlanInstallmentCard card={card as PaymentPlanInstallmentCardResponse} />,
        };
      case 'MoveFileCreated':
        return {
          title: fields.moveFileCreatedCard?.title.value ?? 'Move File Created [not in Sitecore]',
          content: <MoveFileCreatedCard />,
        };
      case 'PushDirectDebit':
        return {
          title: fields.pushDirectDebitCard?.title.value ?? 'Push Direct Debit [not in Sitecore]',
          content: <PushDirectDebitCard card={card as PushDirectDebitCardResponse} />,
        };
      case 'ProductConfirmation':
        return {
          title: fields.productConfirmationCard?.title.value ?? 'Product Confirmation [not in Sitecore]',
          content: <ProductConfirmationCard card={card as ProductConfirmationCardResponse} />,
        };
      case 'CompleteMoveFile':
        return {
          title: fields.completeMoveFileCard?.title.value ?? 'Complete Move File [not in Sitecore]',
          content: <CompleteMoveFileCard />,
        };
      case 'ReferToAccountNumberIfRefund':
        return {
          title:
            fields.referToAccountNumberIfRefundCard?.title.value ??
            'Refer To Account Number If Refund [not in Sitecore]',
          content: <ReferToAccountNumberIfRefundCard card={card as ReferToAccountNumberIfRefundCardResponse} />,
        };
      case 'AdvanceAmountChanged':
        return {
          title: fields.advanceAmountChangedCard?.title?.value ?? 'Advance Amount Changed [not in Sitecore]',
          content: <AdvanceAmountChangedCard card={card as AdvanceAmountChangedCardResponse} />,
        };
      case 'ThankYou':
        return {
          title: fields.thankYouCard?.title.value ?? 'Thank You [not in Sitecore]',
          content: <ThankYouCard />,
        };
      case 'OrderStatus': {
        return {
          title: fields.orderStatusCard?.title.value,
          content: <OrderStatusCard card={card as OrderStatusCardResponse} />,
        };
      }
      case 'ProgressTimeline':
        return {
          title: fields.progressTimelineCard?.title.value ?? 'Progress Timeline [not in Sitecore]',
          content: <ProgressTimelineCard />,
        };
      case 'CompletedProgressTimeline':
        return {
          title: fields.completedProgressTimelineCard?.title?.value ?? 'Progress Timeline [not in Sitecore]',
          content: <CompletedProgressTimelineCard />,
        };
      case 'BrandBenefits':
        return {
          title: fields.brandBenefitsCard?.title.value ?? 'Brand Benefits [not in Sitecore]',
          content: <BrandBenefitsCard />,
        };
      case 'MarketingProposition':
        return {
          title: fields.marketingPropositionCard?.title?.value ?? 'Bij Eneco kies je voor [not in Sitecore]',
          content: <MarketingPropositionCard />,
        };
      case 'AnticipationAnnouncement':
        return {
          title: fields.anticipationAnnouncementCard?.title.value ?? 'Anticipation Announcement [not in Sitecore]',
          content: <AnticipationAnnouncementCard />,
        };
      case 'DeliveryCountdown': {
        const startDate = new Date((card as OrderStatusCardResponse).details.startDate ?? new Date());
        const today = startOfDay(new Date());
        const amountOfDays = differenceInCalendarDays(startDate, today);

        const title = format(fields.deliveryCountdownCard?.title.value || 'Order Status [not in Sitecore]', {
          days: amountOfDays,
        });

        return {
          title: title,
          content: <DeliveryCountDownCard card={card as DeliveryCountdownCardResponse} />,
        };
      }
      case 'Faq':
        return {
          title: fields.faqCard?.title.value ?? 'Delivery Countdown [not in Sitecore]',
          content: <FaqCard fields={fields.faqCard} />,
        };
      case 'PreDeliveryFAQ':
        return {
          title: fields.preDeliveryFAQCard?.title?.value ?? 'Delivery Countdown [not in Sitecore]',
          content: <FaqCard fields={fields.preDeliveryFAQCard} />,
        };
      case 'PaymentMethodChangedDueToFailedDirectDebit':
        return {
          title: fields.paymentMethodChangeFailedCard?.title?.value,
          content: <PaymentMethodChangeFailedCard />,
        };
      case 'ShowUsage':
        return {
          title: fields.showUsageCard?.title?.value ?? 'Verbruik [not in sitecore]',
          content: <ShowUsageCard card={card as ShowUsageCardResponse} addressIdentifier={addressIdentifier} />,
        };
      case 'MonthlyBilling':
        return {
          title: fields.monthlyBillingCard?.title?.value ?? 'Monthly Billing [not in sitecore]',
          content: <MonthlyBillingCard />,
        };
      default:
        return {
          title: card.key || '',
          content: (
            <Bucket.Content>
              <div>Content coming soon [not in Sitecore]</div>
            </Bucket.Content>
          ),
        };
    }
  }, [card, addressIdentifier, fields, format, isB2C]);

  if (isMissingRequiredCardDetails(card)) {
    return null;
  }

  const { title, content } = getCardContent();
  return <Bucket title={title}>{content}</Bucket>;
};

export const StyledDashboardBlock = styled('div', {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '$6',
  padding: '$6',
});
